import { useEffect, useRef } from 'react';
import { isNil, isNumber } from 'lodash';
import { animateScroll as scroll } from 'react-scroll';
import { useFirstMountState, useUnmount } from 'react-use';
import { useRouter } from 'next/router';
import { getWindow } from '@surfline/web-common';

// added timeout for load race conditions issues
const WAIT_FOR_FIRST_RENDER = 500;

const updateScroll = (initialStartDayIndex: number = 0) => {
  const win = getWindow();
  const element =
    win?.document?.getElementById(`forecast-day-${initialStartDayIndex + 1}`) ??
    win?.document?.getElementById('forecast');
  if (element) {
    const elementRect = element.getBoundingClientRect();
    const scrollTop = win?.pageYOffset ?? win?.document?.documentElement?.scrollTop;
    const elementScrollTop = (elementRect?.top ?? 0) + scrollTop;
    scroll.scrollTo(elementScrollTop, {
      duration: 500,
      smooth: true,
    });
  }
};

const scrollToTop = () => {
  scroll.scrollToTop({
    duration: 300,
    smooth: true,
  });
};

const useSpotPageScroll = (setIsNavigating?: (isNavigating: boolean) => void) => {
  const win = getWindow();
  const router = useRouter();
  const isFirstMount = useFirstMountState();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevInitialStartDayIndexRef = useRef<string | undefined>(undefined);

  // Handle route change scroll to top
  useEffect(() => {
    if (!setIsNavigating) return undefined;

    const handleRouteChangeStart = (url: string, { shallow }: { shallow: boolean }) => {
      // Only set navigating if it's not a shallow route change
      if (!shallow) {
        setIsNavigating(true);
        // Scroll to top immediately when navigation starts
        // This provides immediate feedback and shows the loader at the top
        scrollToTop();
      }
    };

    const handleRouteChangeComplete = (url: string, { shallow }: { shallow: boolean }) => {
      // Only clear navigating if it's not a shallow route change
      if (!shallow) {
        setIsNavigating(false);
      }
    };

    const handleRouteChangeError = (url: string, { shallow }: { shallow: boolean }) => {
      // Only clear navigating if it's not a shallow route change
      if (!shallow) {
        setIsNavigating(false);
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router, setIsNavigating]);

  // Handle initial day index scrolling
  useEffect(() => {
    const initialStartDayIndex = router?.query?.initialStartDayIndex;
    const prevInitialStartDayIndex = prevInitialStartDayIndexRef.current;

    // Only scroll if initialStartDayIndex has actually changed
    if (
      initialStartDayIndex !== prevInitialStartDayIndex &&
      !isNil(initialStartDayIndex) &&
      !isNumber(initialStartDayIndex) &&
      Number.isFinite(Number(initialStartDayIndex))
    ) {
      if (isFirstMount && win.location.hash === '') {
        // on first mount only scroll if hash is not added and initialStartDayIndex is a valid number
        timeoutRef.current = setTimeout(
          () => updateScroll(Number(initialStartDayIndex)),
          WAIT_FOR_FIRST_RENDER,
        );
      } else if (!isFirstMount) {
        // after first mount, scroll page if initialStartDayIndex is a valid number
        updateScroll(Number(initialStartDayIndex));
      }
    }

    // Update the ref with the current value
    prevInitialStartDayIndexRef.current = initialStartDayIndex as string;
  }, [isFirstMount, win?.location?.hash, router?.query?.initialStartDayIndex]);

  useUnmount(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  });
};

export default useSpotPageScroll;
